import apiBaseState from "~/constants/apiBaseState";
import deepClone from "~/utils/deepClone";
import Vueset from "~/utils/Vueset";

export const state = () => ({
    discussionForumOverview: apiBaseState(),
    discussionForumQuestionResponses: apiBaseState(),
    currentQuestionNumber: null,
    messageToReplyTo: null,
    messageToEdit: null,
    pagination: {},
    taskId: null,
    isClient: false,
    loadingReplies: false,
    loadingNewQuestion: false,
    loadingRepliesId: null,
});

export const getters = {
    currentQuestionOverview(state) {
        if(!state.discussionForumOverview.data || (state.currentQuestionNumber === null)){
            return null;
        }
        return state.discussionForumOverview.data.questions[state.currentQuestionNumber - 1];
    },
    questionCount(state){
        if(!state.discussionForumOverview.data){
            return null;
        }
        return state.discussionForumOverview.data.questions.length;
    },
    isClosed(state){
        if(!state.discussionForumOverview.data){
            return null;
        }
        return state.discussionForumOverview.data.status === 'closed';
    },
    isCompleted(state){
        if(!state.discussionForumOverview.data){
            return null;
        }
        return state.discussionForumOverview.data.completed_at;
    }
};

export const mutations = {
    SET_IS_LOADING_NEW_QUESTION(state, payload){
        state.loadingNewQuestion = payload;
    },
    SET_IS_CLIENT(state, payload){
        state.isClient = payload;
    },
    SET_REPLIES_LOADING(state, payload){
        state.loadingReplies = payload;
    },
    SET_REPLIES_LOADING_ID(state, payload){
        state.loadingRepliesId = payload;
    },
    RESET_DISCUSSION_FORUM(state) {
        state.discussionForumOverview = apiBaseState();
        state.discussionForumQuestionResponses = apiBaseState();
        state.currentQuestionNumber = null;
        state.messageToReplyTo = null;
        state.messageToEdit = null;
        state.loadingReplies = false;
        state.pagination = {};
    },
    SOFT_RESET_DISCUSSION_FORUM(state) {
        state.discussionForumQuestionResponses = apiBaseState();
        state.currentQuestionNumber = null;
        state.messageToReplyTo = null;
        state.messageToEdit = null;
        state.loadingReplies = false;
        state.pagination = {};
    },
    SET_DISCUSSION_FORUM_OVERVIEW(state, data) {
        state.discussionForumOverview = data;
    },
    SET_CURRENT_QUESTION_NUMBER(state, data) {
        state.currentQuestionNumber = parseInt(data);
    },
    SET_DISCUSSION_FORUM_PROPERTY(state, payload) {
        state.discussionForumOverview.data = {
            ...state.discussionForumOverview.data,
            ...payload
        };
    },
    SET_DISCUSSION_FORUM_QUESTION_RESPONSES(state, payload) {
        if(payload.data){
            payload.data.data.forEach(message => {
                message.level = 1;
            });
            Vueset(state.pagination, 'level_root', {
                nextPage: payload.data.meta.last_page > payload.data.meta.current_page ? payload.data.meta.current_page + 1 : null,
                prevPage: payload.data.meta.current_page > 1 ? payload.data.meta.current_page - 1 : null,
                from: payload.data.meta.from,
                to: payload.data.meta.to,
                total: payload.data.meta.total,
            });
        }
        state.discussionForumQuestionResponses = payload;
    },
    ADD_DISCUSSION_FORUM_SINGLE_MESSAGE(state, {response, chainId, questionId}) {
        this.commit("discussionForum/UPDATE_DISCUSSION_FORUM_STATUS", response.task);
        const isLevel1 = !chainId;
        let responses = state.discussionForumQuestionResponses.data.data;
        if(isLevel1){
            if(response.data.pinned){
                const quId = state.discussionForumOverview.data.questions.findIndex(question => question.id === questionId);
                state.discussionForumOverview.data.questions[quId].pinned_message = response.data;
            }else{
                responses.unshift(response.data);
            }
            return;
        }
        chainId.forEach((id, index) => {
            const matchingIndex = responses.findIndex(question => question.id === id);
            const isLastIteration = index === chainId.length - 1;
            if(isLastIteration){
                responses[matchingIndex].replies.unshift({...response.data, level: index + 2});
            }else{
                responses = responses[matchingIndex].replies;
            }
        });
    },
    UPDATE_DISCUSSION_FORUM_SINGLE_MESSAGE(state, {response, chainId, questionId}) {
        this.commit("discussionForum/UPDATE_DISCUSSION_FORUM_STATUS", response.task);

        if(response.data.pinned){
            const quId = state.discussionForumOverview.data.questions.findIndex(question => question.id === questionId);
            state.discussionForumOverview.data.questions[quId].pinned_message = response.data;
            return;
        }

        let responses = state.discussionForumQuestionResponses.data.data;
        chainId.forEach((id, index) => {
            const matchingIndex = responses.findIndex(question => question.id === id);
            const isLastIteration = index === chainId.length - 1;
            if(isLastIteration){
                Vueset(responses, matchingIndex, {...response.data, level: index + 1});
            }else{
                responses = responses[matchingIndex].replies;
            }
        });
    },
    DELETE_DISCUSSION_FORUM_SINGLE_MESSAGE(state, {chainId, pinned, questionId}) {
        if(pinned){
            const quId = state.discussionForumOverview.data.questions.findIndex(question => question.id === questionId);
            state.discussionForumOverview.data.questions[quId].pinned_message = null;
            return;
        }
        let responses = state.discussionForumQuestionResponses.data.data;
        let parent = null;
        chainId.forEach((id, index) => {
            const matchingIndex = responses.findIndex(question => question.id === id);
            const isLastIteration = index === chainId.length - 1;

            if(isLastIteration){
                responses.splice(matchingIndex, 1);

                if(parent){
                    Vueset(parent, 'replies_count', parent.replies_count -= 1);
                }
            }else{
                parent = responses[matchingIndex];
                responses = responses[matchingIndex].replies;
            }
        });
    },
    ADD_DISCUSSION_FORUM_MESSAGES(state, {replies, chainId, meta, isLoadMore, isPrev}){
        if(state.discussionForumQuestionResponses.data.task){
            this.commit("discussionForum/UPDATE_DISCUSSION_FORUM_STATUS", state.discussionForumQuestionResponses.data.task);
        }
        const isRoot = !chainId.length;
        const path = !isRoot ? `level_${chainId.join('_')}` : 'level_root';
        //If the pagination path is already created then increase/decrease one of the counts
        if(state.pagination[path]){
            const value = state.pagination[path];
            if(isPrev){
                value.prevPage = meta.current_page > 1 ? meta.current_page - 1 : null;
                value.from = meta.from;
            }else{
                value.nextPage = meta.last_page > meta.current_page ? meta.current_page + 1 : null;
                value.to = meta.to;
            }
            Vueset(state.pagination, path, value);
        }else{
            Vueset(state.pagination, path, {
                nextPage: meta.last_page > meta.current_page ? meta.current_page + 1 : null,
                prevPage: meta.current_page > 1 ? meta.current_page - 1 : null,
                from: meta.from,
                to: meta.to,
                total: meta.total,
            });
        }

        let responses = state.discussionForumQuestionResponses.data.data;
        if(isRoot){
            const value = isPrev
                ? [...replies, ...deepClone(responses)]
                : [...deepClone(responses), ...replies];
            Vueset(state.discussionForumQuestionResponses.data, 'data', value);
        }
        chainId.forEach((id, index) => {
            const matchingIndex = responses.findIndex(question => question.id === id);
            const isLastIteration = index === chainId.length - 1;
            if(isLastIteration){
                replies.forEach(reply => {
                    reply.level = index + 2;
                });
                let allReplies = [];
                if(isLoadMore){
                    allReplies = isPrev
                        ? [...replies, ...deepClone(responses[matchingIndex].replies)]
                        : [...deepClone(responses[matchingIndex].replies), ...replies];
                    // Vueset(state.discussionForumQuestionResponses.data, 'data', value);
                }
                Vueset(responses[matchingIndex], 'replies', isLoadMore ? allReplies : replies);

            }else{
                responses = responses[matchingIndex].replies;
            }
        });

    },
    SET_MESSAGE_TO_REPLY_TO(state, data) {
        state.messageToReplyTo = data;
    },
    SET_MESSAGE_TO_EDIT(state, data) {
        state.messageToEdit = data;
    },
    INCREASE_REPLY_COUNT_FOR_MESSAGE(state, {messageId, parentId}){
        const rootData = state.discussionForumQuestionResponses.data.data;

        if(parentId){
            const index = rootData.findIndex(question => question.id === parentId);
            const childIndex = rootData[index].replies.findIndex(question => question.id === messageId);
            rootData[index].replies[childIndex].replies_count += 1;
        }else{
            const index = rootData.findIndex(question => question.id === messageId);
            rootData[index].replies_count += 1;
        }
    },
    DELETE_REPLIES_FROM_MESSAGE(state, {messageId, parentId}){
        const isLevel2 = parentId === null && messageId;
        const isLevel3 = parentId !== null && messageId;

        if(isLevel2){
            const parentMessageIndex = state.discussionForumQuestionResponses.data.data.findIndex(question => question.id === messageId);
            Vueset(state.discussionForumQuestionResponses.data.data[parentMessageIndex], 'replies', []);

        }
        if(isLevel3){
            const parentMessageIndex = state.discussionForumQuestionResponses.data.data.findIndex(question => question.id === parentId);
            const childMessageIndex = state.discussionForumQuestionResponses.data.data[parentMessageIndex].replies.findIndex(question => question.id === messageId);
            Vueset(state.discussionForumQuestionResponses.data.data[parentMessageIndex].replies[childMessageIndex], 'replies', []);
        }
    },
    CLEAR_PAGINATION_FOR_RESPONSE(state, {chainId}){
        const isRoot = !chainId.length;
        const path = !isRoot ? `level_${chainId.join('_')}` : 'level_root';
        delete state.pagination[path];
    },
    UPDATE_DISCUSSION_FORUM_MESSAGE_LIKES(state, {chainId, payload}){
        let responses = state.discussionForumQuestionResponses.data.data;
        chainId.forEach((id, index) => {
            const matchingIndex = responses.findIndex(question => question.id === id);
            const isLastIteration = index === chainId.length - 1;
            if(isLastIteration){
                responses[matchingIndex].likes = payload;
            }else{
                responses = responses[matchingIndex].replies;
            }
        });
    },
    UPDATE_DISCUSSION_FORUM_REPORTED_BY_USER(state, {chainId, payload}){
        let responses = state.discussionForumQuestionResponses.data.data;
        chainId.forEach((id, index) => {
            const matchingIndex = responses.findIndex(question => question.id === id);
            const isLastIteration = index === chainId.length - 1;
            if(isLastIteration){
                responses[matchingIndex].reported_by_user = payload;
            }else{
                responses = responses[matchingIndex].replies;
            }
        });
    },
    UPDATE_DISCUSSION_FORUM_MESSAGE_MEDIA(state, {chainId, payload}){
        let responses = state.discussionForumQuestionResponses.data.data;
        chainId.forEach((id, index) => {
            const matchingIndex = responses.findIndex(question => question.id === id);
            const isLastIteration = index === chainId.length - 1;
            if(isLastIteration){
                responses[matchingIndex].media = payload;
            }else{
                responses = responses[matchingIndex].replies;
            }
        });
    },
    UPDATE_DISCUSSION_FORUM_STATUS(state, {completed_at, status}){
        state.discussionForumOverview.data.status = status;
        state.discussionForumOverview.data.completed_at = completed_at;
    },
    UPDATE_DISCUSSION_FORUM_TASK_ID(state, id){
        state.taskId = id;
    },
};

export const actions = {
    GET_DISCUSSION_FORUM_OVERVIEW(state, {taskSlug}) {
        const url = `/participant/task/${taskSlug}/discussion-forum`;

        return this.$whyApi.get({
            url,
            mutation: "discussionForum/SET_DISCUSSION_FORUM_OVERVIEW",
            errorMessage: "Error getting discussion forum data"
        });
    },
};
